import { useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAtomValue, useSetAtom } from "jotai";
import { clearAuthStateAtom, tokenAtom } from "@/atoms/authAtoms";
import { Button, cn } from "@/design-system/components/react";
import { authService } from "@/services/auth";
import { decodeToken } from "@/utils/tokenUtils";
import { type PushAlerts, unsubscribeBrowser } from "./usePushAlerts";

/** Where sign-in goes after a Notify sign out (Login reads `state.from`, also
 * through Google's `auth_redirect`), so the installed app comes back to Notify. */
export const NOTIFY_HOME = "/notify/";

/** The signed-in email from the token, if it has one (the local-dev token doesn't). */
export function useSignedInEmail(): string | undefined {
  const token = useAtomValue(tokenAtom);
  return token ? decodeToken(token)?.email : undefined;
}

/**
 * Sign out from Notify: turns this account's alerts off on this device first
 * (server row + unsubscribe; it needs the token, so before logout), and drops the
 * browser subscription in every state, also when the server can't be reached,
 * then the same logout as web-demo's (cookies, query cache, token
 * revoke, auth state), then sign-in with a way back to /notify/. The browser's
 * notification permission is left as it is, so the next account only taps
 * Enable alerts. web-demo's `useAuth().logout` isn't reused: it goes to /login
 * with no `state.from`, so sign-in would land on the role's web-demo page.
 * The Google (Cognito Hosted UI) session is not cleared, as on web-demo.
 */
export function useNotifySignOut(alerts: PushAlerts) {
  const clearAuth = useSetAtom(clearAuthStateAtom);
  const navigate = useNavigate();
  const signingOut = useRef(false);
  return useCallback(async () => {
    if (signingOut.current) return;
    signingOut.current = true;
    const { state } = alerts;
    // States with a server row to delete: on, a failed Turn off (as
    // AlertsControl) and a failed re-sync. disable() never throws: a failed
    // delete leaves it in error/disable with the browser subscription still there.
    if (state.kind === "on" || (state.kind === "error" && state.during !== "enable"))
      await alerts.disable();
    // So, whatever the state (that one, checking, asking, no site), the browser
    // subscription goes too: the next account here would get it on open, with no
    // tap. No subscription is a no-op; a server row left behind gets 410 next send.
    await unsubscribeBrowser().catch(() => {});
    await authService.logout();
    clearAuth();
    navigate("/login", { replace: true, state: { from: { pathname: NOTIFY_HOME } } });
  }, [alerts, clearAuth, navigate]);
}

export function AccountEmail({ email, className }: { email?: string; className?: string }) {
  if (!email) return null;
  return (
    <span title={email} className={cn("min-w-0 truncate text-xs text-fg-muted", className)}>
      {email}
    </span>
  );
}

export function SignOutButton({
  onSignOut,
  className,
}: {
  onSignOut: () => void;
  className?: string;
}) {
  return (
    <Button
      size="sm"
      variant="ghost"
      className={cn("h-8 px-2 text-[12.5px] text-fg-muted", className)}
      onClick={onSignOut}
    >
      Sign out
    </Button>
  );
}
